// Pure display helpers for the AI analysis report. No Vue, no store, no '@/' imports:
// these run under `node --test` as well as in the browser bundle.
import { APPROVAL, withAlpha } from './findingsMetrics.js';
import { componentTypeOf } from './findingComponents.js';

export const EM_DASH = '—';
const PERCENT = 100;

// Confidence rings (approved report showcase): green >= 85, amber 70-84, red < 70.
export const RING_BANDS = Object.freeze({ HIGH: 85, MID: 70 });
export const RING_RADIUS = 15;
export const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

// Polygon contour buckets (same thresholds on the 0..1 scale).
export const CONTOUR_THRESHOLDS = Object.freeze({ HIGH: 0.85, MODERATE: 0.7 });

/** 'high' | 'mid' | 'low' for a 0..1 confidence. */
export function ringBand(value) {
  const percent = Math.round((Number(value) || 0) * PERCENT);
  if (percent >= RING_BANDS.HIGH) return 'high';
  if (percent >= RING_BANDS.MID) return 'mid';
  return 'low';
}

/** SVG stroke-dasharray for a ring filled to `value` (0..1). */
export function ringArc(value) {
  const fraction = Math.min(1, Math.max(0, Number(value) || 0));
  return `${(RING_CIRCUMFERENCE * fraction).toFixed(1)} ${RING_CIRCUMFERENCE.toFixed(1)}`;
}

/**
 * Contour colours, keyed by bucket. These mirror --aa-contour-* in _aiAnalysisTokens.scss, but the viewer
 * takes a literal stroke rather than a CSS variable, so the hexes are repeated here on purpose.
 */
export const CONTOUR_COLORS = Object.freeze({ high: '#d32f2f', moderate: '#ef8a17', low: '#e6b400' });

/** 'high' | 'moderate' | 'low' for the polygon confidence. */
export function contourBucket(polygonConfidence) {
  const value = Number(polygonConfidence) || 0;
  if (value >= CONTOUR_THRESHOLDS.HIGH) return 'high';
  if (value >= CONTOUR_THRESHOLDS.MODERATE) return 'moderate';
  return 'low';
}

/** Polygon outline colour for a finding's polygon confidence — what the report Legend documents. */
export function contourColor(polygonConfidence) {
  return CONTOUR_COLORS[contourBucket(polygonConfidence)];
}

/** 0..1 -> "88%" (or "87.6%" with decimals); a dash when the value is missing. */
export function formatPercent(value, decimals = 0) {
  if (value === null || value === undefined || value === '') return EM_DASH;
  const number = Number(value);
  if (!Number.isFinite(number)) return EM_DASH;
  return `${(number * PERCENT).toFixed(decimals)}%`;
}

const isDecided = (finding) => finding.approvalStatus === APPROVAL.ACCEPTED || finding.approvalStatus === APPROVAL.REJECTED;

/** Findings without a decision. Accepted and Rejected both count as decided (the API soft-deletes rejects). */
export function pendingFindings(findings) {
  return (findings || []).filter((f) => !isDecided(f));
}

export function decidedCount(findings) {
  return (findings || []).filter(isDecided).length;
}

/** "Column C1 – Front face" -> "Column C1" (en dash or hyphen separators); a dash when empty. */
export function componentOf(locationDescription) {
  const text = String(locationDescription || '').trim();
  if (!text) return EM_DASH;
  return text.split(/\s+[–-]\s+/)[0].trim() || EM_DASH;
}

/**
 * The AVA Inspect's own master tag as a suggested component ({ id, name, type }); null for full-scan jobs or an
 * AVA Inspect without one. Shown as a suggestion in the picker and the detail panel, never applied on its own.
 */
export function suggestedTagFor(job, avaInspectsById) {
  if (!job || !job.avaInspectId) return null;
  const tag = (avaInspectsById || {})[job.avaInspectId];
  const master = (tag && tag.masterTag) || null;
  const id = (master && master.id) || (tag && tag.masterTagId) || null;
  if (!id) return null;
  return {
    id: String(id),
    name: (master && master.name) || (tag && tag.masterTagName) || String(id),
    type: componentTypeOf(master ? master.type || master.discriminator : null),
  };
}

export const VIEWER_FILL_ALPHA = 0.25;

/**
 * Report polygons are drawn thin (Khalid, 2026-09-07): the viewer's 3px default reads as paint on the asset and
 * buries the corrosion it is pointing at. Solid, not dashed - a dashed contour was tried and rejected. Only the
 * report sets this; AI Inspect keeps the viewer's defaults.
 */
export const VIEWER_STROKE_WIDTH = '1.5px';
export const VIEWER_STROKE_WIDTH_ACTIVE = '2.5px';

/**
 * One scan's findings shaped for ViewersSection's `inspects` prop (report mode): rejected areas leave the viewer,
 * badge numbers stay those of the full table, the polygon confidence colours the outline (the scheme the report
 * Legend documents), the active area gets the heavier outline. Returns new objects; the findings are not touched.
 */
export function viewerInspectsFor(findings, imageId, activeId) {
  return (findings || [])
    .filter((f) => f.imageId === imageId && f.approvalStatus !== APPROVAL.REJECTED)
    .map((f) => {
      const stroke = contourColor(f.polygonConfidence);
      const isActive = f.id === activeId;
      return {
        ...f,
        useMaskPoints: true,
        isActive,
        strokeColor: stroke,
        fillColor: withAlpha(stroke, VIEWER_FILL_ALPHA),
        strokeWidth: isActive ? VIEWER_STROKE_WIDTH_ACTIVE : VIEWER_STROKE_WIDTH,
      };
    });
}
